/* eslint-disable prefer-arrow-callback, react/require-default-props */
import React, {
  useState, useEffect, useRef,
} from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
} from '@mui/material';
import { MdEdit } from 'react-icons/md';
import { VariableSizeList } from 'react-window';
import { confirmWrapper } from 'src/components/Dialog';
import {
  CloseActiveIcon,
  CloseSquareIcon,
  ConfirmActiveIcon,
  ConfirmSquareIcon,
} from 'src/icons';
import { Option } from '../../types';

function renderRow(props: any) {
  const { data, index, style } = props;
  const dataSet = data[index];
  return (
    <div style={style}>
      {dataSet}
    </div>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

interface ListboxComponentProps extends React.HTMLAttributes<HTMLElement> {
  itemSize?: number;
}

const ListboxComponent = React.forwardRef<HTMLDivElement, ListboxComponentProps>(
  function ListboxComponent(props, ref) {
    const { children, itemSize = 48, ...other } = props;
    const itemData: any[] = [];
    (children as any[]).forEach((item) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    });

    const itemCount = itemData.length;

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={Math.min(itemCount * itemSize, 400)}
            width="100%"
            outerElementType={OuterElementType}
            itemCount={itemCount}
            itemSize={() => itemSize}
            overscanCount={5}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  },
);

const areArraysEqual = (array1: Option[], array2: Option[]) => {
  if (array1.length !== array2.length) return false;

  const isObjectEqual = (obj1: Option, obj2: Option) => (
    obj1.label === obj2.label && obj1.value === obj2.value);

  for (let i = 0; i < array1.length; i += 1) {
    if (!isObjectEqual(array1[i], array2[i])) {
      return false;
    }
  }

  return true;
};

const InlineMultiSelect = function (props: any) {
  const {
    value = [],
    label,
    options = [],
    confirmFunction,
  } = props;
  const prevValueRef = useRef();

  const [tempValue, setTempValue] = useState(value);
  const [newValue, setNewValue] = useState(value);
  const [editMode, togleEditMode] = useState(false);

  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isConfirmHovered, setIsConfirmHovered] = useState(false);

  const handleMouseEnterClose = () => {
    setIsCloseHovered(true);
  };

  const handleMouseLeaveClose = () => {
    setIsCloseHovered(false);
  };

  const handleMouseEnterConfirm = () => {
    setIsConfirmHovered(true);
  };

  const handleMouseLeaveConfirm = () => {
    setIsConfirmHovered(false);
  };

  useEffect(() => {
    if (prevValueRef.current !== undefined && !areArraysEqual(prevValueRef.current, value)) {
      setNewValue(value);
      setTempValue(undefined);
      togleEditMode(false);
    }
    prevValueRef.current = value;
  }, [value]);

  const customFilterOptions = (dropDownOptions: any, state: any) => {
    const { inputValue = '' } = state;
    return dropDownOptions.filter(({ label: title = '', subLabel: subTitle = '' }: any) => (
      title.toLowerCase().includes(inputValue.toLowerCase())
      || subTitle.toLowerCase().includes(inputValue.toLowerCase())
    ));
  };

  const handleChange = (e: any, updatedValue: any) => {
    setTempValue((prevValue: any) => {
      if (prevValue.length === 1 && updatedValue.length === 0) {
        return prevValue;
      }

      return updatedValue;
    });
  };

  const onDelete = (optionValue: any) => () => {
    setTempValue((prevOptions: any) => prevOptions.filter((v: any) => v.value !== optionValue));
  };

  const onEdit = () => {
    setTempValue(newValue);
    togleEditMode((prevState) => !prevState);
  };

  const onCancel = () => {
    setTempValue(undefined);
    togleEditMode(false);
  };

  const onClickUpdate = async () => {
    const confirmation = await confirmWrapper(
      `Are you sure you want to update ${label}?`,
      {
        variant: 'warning',
        primaryBtnLabel: 'Proceed',
        secondaryBtnLabel: 'Cancel',
      },
    );

    if (confirmation) {
      try {
        await confirmFunction(tempValue);
        setNewValue(tempValue);
        togleEditMode(false);
      } catch (e) {
        console.log(e);
      }
    }
  };

  return (
    <Box className="w-full h-full relative group">
      {label && (
        <Box className="flex items-center gap-3">
          <Box className="text-sm leading-5 text-[#828A97] pb-1">
            {label}
          </Box>
          {!editMode && (
            <Box
              className="cursor-pointer group-hover:visible invisible"
              onClick={onEdit}
            >
              <MdEdit size="14" color="#828A97" />
            </Box>
          )}
        </Box>
      )}
      {
        editMode ? (
          <Box className="relative w-full h-full">
            <Box
              sx={{
                '& > :not(:last-child)': { mr: 1 },
                '& > *': { mr: 1 },
              }}
            >
              {tempValue.map((v: any) => (
                <Chip
                  className="mb-1"
                  key={v.value}
                  label={v.label}
                  onDelete={tempValue.length > 1 ? onDelete(v.value) : undefined}
                />
              ))}
            </Box>
            <Autocomplete
              multiple
              options={options}
              ListboxComponent={ListboxComponent}
              renderOption={(params, option) => (
                <li {...params}>
                  <Box className="flex flex-col">
                    <Box>{option.label}</Box>
                    {option.subLabel && (
                      <Box className="text-xs">
                        {option.subLabel}
                      </Box>
                    )}
                  </Box>
                </li>
              )}
              filterOptions={customFilterOptions}
              onChange={handleChange}
              disableCloseOnSelect
              disableClearable
              value={tempValue}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="w-full h-full"
                  placeholder={label}
                  onKeyDown={(event: any) => {
                    if (event.key === 'Backspace') {
                      event.stopPropagation();
                    }
                  }}
                />
              )}
              sx={{
                '& .MuiOutlinedInput-root': {
                  padding: '0px!important',
                },
              }}
            />

            <Box className="absolute flex gap-[2px] right-0">
              <Box
                className="cursor-pointer"
                onMouseEnter={handleMouseEnterClose}
                onMouseLeave={handleMouseLeaveClose}
                onClick={onCancel}
              >
                {isCloseHovered ? (
                  <CloseActiveIcon size={20} />
                ) : (
                  <CloseSquareIcon size={20} />
                )}
              </Box>
              <Box
                className="cursor-pointer"
                onMouseEnter={handleMouseEnterConfirm}
                onMouseLeave={handleMouseLeaveConfirm}
                onClick={onClickUpdate}
              >
                {isConfirmHovered ? (
                  <ConfirmActiveIcon size={20} />
                ) : (
                  <ConfirmSquareIcon size={20} />
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box className="flex">
            <Box
              sx={{
                '& > :not(:last-child)': { mr: 1 },
                '& > *': { mr: 1 },
              }}
              className="text-base overflow-hidden text-ellipsis"
            >
              {newValue.map((v: any) => (
                <Chip
                  className="mb-1"
                  key={v.value}
                  label={v.label}
                />
              ))}
            </Box>
          </Box>
        )
      }
    </Box>
  );
};

export default InlineMultiSelect;
